import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Sparkles,
  Clock,
  TrendingUp,
  Command,
  BookOpen,
  LifeBuoy,
  Zap
} from 'lucide-react';
import CommandPalette from './CommandPalette';
import { useNavigate } from 'react-router-dom';

const HelpCenter = () => {
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K handler
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Set page title
  useEffect(() => {
    document.title = "Help Center — SurFox";
  }, []);

  const handleNavigate = (link, section) => {
    if (link && section) {
      // Navigate and trigger section expansion
      navigate(link);
      // Dispatch event to expand section
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('tour-expand-section', {
          detail: { sectionId: section }
        }));
      }, 300);
    } else if (link) {
      navigate(link);
    }
  };

  const popularTopics = [
    {
      icon: '📱',
      title: 'Add a Phone Number',
      description: 'Purchase and configure numbers for campaigns',
      query: 'add phone number'
    },
    {
      icon: '🛡️',
      title: 'A2P Registration',
      description: 'Complete carrier compliance requirements',
      query: 'a2p registration'
    },
    {
      icon: '🚀',
      title: 'Create a Campaign',
      description: 'Set up AI-powered lead engagement',
      query: 'create campaign'
    },
    {
      icon: '🔥',
      title: 'Hot Leads',
      description: 'Understanding AI lead scoring',
      query: 'what is hot lead'
    },
    {
      icon: '⚠️',
      title: 'Messages Not Sending',
      description: 'Troubleshoot delivery issues',
      query: 'messages not sending'
    },
    {
      icon: '👥',
      title: 'Add Team Members',
      description: 'Invite and manage your sales team',
      query: 'add team member'
    }
  ];

  const quickLinks = [
    { icon: Sparkles, label: 'Quick Start Guide', action: () => setIsCommandOpen(true) },
    { icon: LifeBuoy, label: 'Troubleshooting', action: () => setIsCommandOpen(true) },
    { icon: BookOpen, label: 'All Features', action: () => setIsCommandOpen(true) },
    { icon: Zap, label: 'Settings Help', action: () => setIsCommandOpen(true) }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 tracking-tight">
              How can we help you?
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 mb-8">
              Get instant answers to your questions
            </p>

            {/* Search CTA */}
            <button
              onClick={() => setIsCommandOpen(true)}
              className="w-full max-w-2xl mx-auto bg-white/10 backdrop-blur-sm hover:bg-white/15 transition-all border border-white/20 rounded-2xl px-6 py-4 flex items-center gap-4 text-left group"
            >
              <Search className="w-5 h-5 text-slate-400 flex-shrink-0" />
              <span className="flex-1 text-slate-300">
                Search help articles, guides, troubleshooting...
              </span>
              <kbd className="hidden sm:flex items-center gap-1 px-3 py-1.5 bg-slate-800/50 border border-slate-700 rounded-lg text-sm font-medium text-slate-300">
                <Command className="w-3 h-3" />
                <span>K</span>
              </kbd>
            </button>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        {/* Popular Topics */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            <h2 className="text-2xl font-bold text-slate-900">Popular Topics</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {popularTopics.map((topic, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => {
                  setInitialQuery(topic.query);
                  setIsCommandOpen(true);
                }}
                className="bg-white rounded-2xl border border-slate-200 p-6 text-left hover:border-slate-300 hover:shadow-lg transition-all group"
              >
                <div className="text-4xl mb-3">{topic.icon}</div>
                <h3 className="font-semibold text-slate-900 mb-2 group-hover:text-orange-600 transition-colors">
                  {topic.title}
                </h3>
                <p className="text-sm text-slate-600">{topic.description}</p>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <Zap className="w-5 h-5 text-slate-600" />
            <h2 className="text-2xl font-bold text-slate-900">Quick Links</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickLinks.map((link, index) => {
              const Icon = link.icon;
              return (
                <button
                  key={index}
                  onClick={link.action}
                  className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-slate-300 hover:shadow-lg transition-all group flex flex-col items-center text-center"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-3 group-hover:from-orange-100 group-hover:to-orange-200 transition-all">
                    <Icon className="w-6 h-6 text-slate-600 group-hover:text-orange-600 transition-colors" />
                  </div>
                  <span className="font-medium text-slate-900">{link.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-5 h-5 text-slate-600" />
            <h2 className="text-2xl font-bold text-slate-900">Getting Started</h2>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
            <div className="p-6">
              <h3 className="font-semibold text-slate-900 mb-2">📋 Platform Setup Checklist</h3>
              <p className="text-sm text-slate-600 mb-4">
                Complete these steps to get your AI texting platform fully operational
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Complete A2P Registration</p>
                    <p className="text-xs text-slate-500">Required for message delivery (3-10 days)</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Purchase Phone Numbers</p>
                    <p className="text-xs text-slate-500">Get local numbers for better response rates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Create Your First Campaign</p>
                    <p className="text-xs text-slate-500">Set up AI-powered lead engagement</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold flex-shrink-0">4</div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">Add Team Members</p>
                    <p className="text-xs text-slate-500">Invite your sales team to receive hot leads</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <h3 className="font-semibold text-slate-900 mb-2">💡 Pro Tip</h3>
              <p className="text-sm text-slate-600">
                Press <kbd className="px-2 py-0.5 bg-slate-100 border border-slate-200 rounded text-xs font-mono">⌘K</kbd> anywhere
                in the app to instantly search for help. It's the fastest way to get answers!
              </p>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="mt-12 text-center">
          <p className="text-slate-600 mb-4">Can't find what you're looking for?</p>
          <button
            onClick={() => setIsCommandOpen(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium"
          >
            <Search className="w-4 h-4" />
            Search All Help Articles
          </button>
        </div>
      </div>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandOpen}
        onClose={() => {
          setIsCommandOpen(false);
          setInitialQuery(''); // Reset query when closed
        }}
        onNavigate={handleNavigate}
        initialQuery={initialQuery}
      />
    </div>
  );
};

export default HelpCenter;
